/**
 * enterpriseUsageService
 *
 * Teacher-facing view of their own enterprise AI usage + safe org aggregates
 * (no other teachers' data). Backs the "vs org average" nudge and the
 * 50/75/90/100% pool-milestone notifications.
 */

export interface MyEnterpriseUsage {
  enterprise: boolean;
  orgName?: string;
  poolLimit?: number;
  orgCreditsUsed?: number;
  orgPct?: number;
  myCredits?: number;
  avgPerTeacher?: number;
  myVsAvgPct?: number;
  showComparison?: boolean;
  periodKey?: string;
}

async function authHeader(): Promise<Record<string, string>> {
  const { getAuth } = await import('firebase/auth');
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('Not signed in.');
  const idToken = await currentUser.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` };
}

export async function getMyEnterpriseUsage(): Promise<MyEnterpriseUsage> {
  const res = await fetch('/.netlify/functions/enterprise-my-usage', {
    method: 'POST',
    headers: await authHeader(),
    body: '{}',
  });
  const data = await res.json().catch(() => ({ enterprise: false }));
  if (!res.ok) return { enterprise: false };
  return data as MyEnterpriseUsage;
}

/** A member of the enterprise org, with their credit usage this period (admin view). */
export interface EnterpriseOrgMember {
  uid: string;
  email: string;
  name: string;
  credits: number;
  redos: number;
  isAdmin: boolean;
}

/** Full org summary for the ENTERPRISE ADMIN (principal). Credits only — never the
 *  org's raw USD cost (that's owner-internal). */
export interface MyEnterpriseOrg {
  enterprise: boolean;
  org?: {
    domain: string;
    name: string;
    logoUrl: string | null;
    seatLimit: number;
    seatCount: number;
    seatsRemaining: number;
    creditPoolLimit: number;
    creditsUsed: number;
    creditsPct: number;
    periodKey: string;
    members: EnterpriseOrgMember[];
  };
}

/** Fetch the caller's enterprise org summary. Returns `{ enterprise: false }` for
 *  anyone who isn't the org's designated admin. */
export async function getMyEnterpriseOrg(): Promise<MyEnterpriseOrg> {
  try {
    const res = await fetch('/.netlify/functions/enterprise-my-org', {
      method: 'POST',
      headers: await authHeader(),
      body: '{}',
    });
    const data = await res.json().catch(() => ({ enterprise: false }));
    if (!res.ok) return { enterprise: false };
    return data as MyEnterpriseOrg;
  } catch {
    return { enterprise: false };
  }
}

/** Pool-usage thresholds (org-wide %) that trigger a one-time teacher notification. */
export const POOL_MILESTONES = [50, 75, 90, 100] as const;

/** The highest milestone reached at `pct`, or null if under the first one. */
export function highestMilestone(pct: number): number | null {
  let hit: number | null = null;
  for (const m of POOL_MILESTONES) if (pct >= m) hit = m;
  return hit;
}
